import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { CONFIG } from '../config';
import { parseJwt } from '../utils/html';
import type { CognitoPayload, AuthUser } from '../types/user';
import api from '../services/api';

/* ──────────────────────────────────────────────────────────
   Auth Context — provides token, user, and auth actions to
   the entire component tree.  Mirrors FE/auth.js behaviour.
   ────────────────────────────────────────────────────────── */

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── helpers ──

function getStoredToken(): string | null {
  let token = sessionStorage.getItem('token');
  if (token) return token;

  // Fallback: cross-tab temporary token (same logic as legacy auth.js)
  const temp = localStorage.getItem('temp_token_for_new_tab');
  const ts = localStorage.getItem('temp_token_timestamp');
  if (temp && ts && Date.now() - parseInt(ts) < 10_000) {
    sessionStorage.setItem('token', temp);
    localStorage.removeItem('temp_token_for_new_tab');
    localStorage.removeItem('temp_token_timestamp');
    return temp;
  }
  localStorage.removeItem('temp_token_for_new_tab');
  localStorage.removeItem('temp_token_timestamp');
  return null;
}

function userFromToken(token: string): AuthUser | null {
  const payload = parseJwt<CognitoPayload>(token);
  if (!payload) return null;
  return {
    sub: payload.sub,
    username: payload['cognito:username'] ?? payload.username ?? '',
    email: payload.email ?? '',
  };
}

async function validateToken(token: string): Promise<boolean> {
  try {
    console.log('[Auth] Validating token...');
    const res = await api.post('/api/auth/validate', { idToken: token });
    const valid = res.data?.valid === true;
    console.log('[Auth] Token valid:', valid);
    return valid;
  } catch (err) {
    console.error('[Auth] Token validation failed:', err);
    return false;
  }
}

async function exchangeCode(code: string): Promise<string | null> {
  try {
    console.log('[Auth] Exchanging code, redirect_uri:', CONFIG.COGNITO_REDIRECT_URI);
    const res = await api.post('/api/auth/callback', {
      code,
      redirect_uri: CONFIG.COGNITO_REDIRECT_URI,
    });
    const idToken = res.data?.tokens?.id_token ?? null;
    console.log(
      '[Auth] Code exchange result:',
      idToken ? 'got token' : 'NO TOKEN in response',
      res.data,
    );
    return idToken;
  } catch (err) {
    console.error('[Auth] Code exchange error:', err);
    return null;
  }
}

// ── Provider ──

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Boot: handle ?code= redirect, then check stored token
  useEffect(() => {
    async function boot() {
      console.log('[Auth] Boot starting, URL:', window.location.href);

      // 1. Handle Cognito redirect (?code=…)
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (code) {
        console.log('[Auth] Found auth code, exchanging...');
        const idToken = await exchangeCode(code);

        if (idToken) {
          sessionStorage.setItem('token', idToken);
          console.log('[Auth] Token stored in sessionStorage');

          // Restore intended URL, or stay at root
          const intended = localStorage.getItem('auth_intended_url');
          localStorage.removeItem('auth_intended_url');

          // Clean up URL (remove ?code= and /home.html)
          window.history.replaceState({}, '', '/');
          console.log('[Auth] URL cleaned to /');

          // Token was just issued — no need to re-validate via the backend.
          // Set authenticated immediately so a transient network error on
          // /api/auth/validate can't log the user out before they even get in.
          setState({
            token: idToken,
            user: userFromToken(idToken),
            isLoading: false,
            isAuthenticated: true,
          });

          if (
            intended &&
            intended !== window.location.origin + '/' &&
            !intended.includes('/home.html')
          ) {
            console.log('[Auth] Restoring intended URL:', intended);
            window.location.replace(intended);
          }
          return;
        } else {
          console.error('[Auth] Code exchange failed — redirecting to login');
          window.location.href = CONFIG.COGNITO_LOGIN_URL;
          return;
        }
      }

      // 2. Check stored token (subsequent page loads)
      const token = getStoredToken();
      console.log('[Auth] Stored token:', token ? 'found' : 'none');

      if (!token) {
        console.log('[Auth] No token — setting unauthenticated');
        setState({ token: null, user: null, isLoading: false, isAuthenticated: false });
        return;
      }

      const valid = await validateToken(token);
      if (valid) {
        console.log('[Auth] Authenticated successfully');
        setState({ token, user: userFromToken(token), isLoading: false, isAuthenticated: true });
      } else {
        // Only clear the token on a definitive invalid response, not on network errors
        // (validateToken already returns false for errors, so check the response directly)
        console.warn('[Auth] Token invalid — clearing');
        sessionStorage.removeItem('token');
        setState({ token: null, user: null, isLoading: false, isAuthenticated: false });
      }
    }

    boot();
  }, []);

  // Periodic token re-validation (every 5 min, same as legacy auth.js)
  useEffect(() => {
    if (!state.isAuthenticated) return;
    const id = setInterval(
      async () => {
        const t = sessionStorage.getItem('token');
        if (t && !(await validateToken(t))) {
          sessionStorage.removeItem('token');
          window.location.href = CONFIG.COGNITO_LOGIN_URL;
        }
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(id);
  }, [state.isAuthenticated]);

  const login = useCallback(() => {
    console.log('[Auth] login() called — redirecting to Cognito');
    // Only save intended URL if it's on the same canonical host (prevents cross-host redirect loops)
    const intended = window.location.href;
    if (!intended.includes('/home.html') && window.location.hostname === location.hostname) {
      localStorage.setItem('auth_intended_url', intended);
    } else {
      localStorage.removeItem('auth_intended_url');
    }
    window.location.href = CONFIG.COGNITO_LOGIN_URL;
  }, []);

  const logout = useCallback(() => {
    console.log('[Auth] logout() called');
    // Clear httpOnly refresh cookie via backend
    api.post('/api/auth/logout').catch(() => {
      /* best-effort */
    });
    sessionStorage.removeItem('token');
    localStorage.removeItem('temp_token_for_new_tab');
    localStorage.removeItem('temp_token_timestamp');
    window.location.href = CONFIG.COGNITO_LOGOUT_URL;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook to access auth state and actions. Must be used inside <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
